// Panel (CONTRACT "Panel and card"): raised step, soft border, radius 8, no
// shadow. Title row 36 with a sentence-case 600 title, muted meta and an end
// slot; body has breathing room (10 x 12); optional foot line.
import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';
import './Panel.css';

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  readonly title?: ReactNode;
  readonly meta?: ReactNode;
  readonly end?: ReactNode;
  readonly foot?: ReactNode;
  readonly headingLevel?: 2 | 3;
  // Removes the body padding for tables and rows that manage their own.
  readonly flush?: boolean;
  readonly children?: ReactNode;
}

export function Panel({ title, meta, end, foot, headingLevel = 2, flush = false, className, children, ...rest }: PanelProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={cx('panel', className)} {...rest}>
      {title !== undefined ? (
        <div className="panel-h">
          <Heading className="panel-t">{title}</Heading>
          {meta !== undefined ? <span className="panel-meta">{meta}</span> : null}
          {end !== undefined ? <div className="panel-end">{end}</div> : null}
        </div>
      ) : null}
      <div className={cx('panel-b', flush && 'panel-b-flush')}>{children}</div>
      {foot !== undefined ? <div className="panel-f">{foot}</div> : null}
    </section>
  );
}

export interface KeyValueListProps {
  readonly items: ReadonlyArray<{ readonly key: string; readonly value: ReactNode; readonly muted?: boolean; readonly wrap?: boolean }>;
  readonly className?: string;
}

// Key value list: values right-aligned in tabular figures.
export function KeyValueList({ items, className }: KeyValueListProps) {
  return (
    <dl className={cx('kv', className)}>
      {items.map((item) => (
        <div className="kv-row" key={item.key}>
          <dt>{item.key}</dt>
          <dd className={cx('num', item.muted && 'kv-muted', item.wrap && 'kv-wrap')}>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
